import { TFunction } from 'i18next';

/**
 * Why the parked documents gave up.
 *
 * `parked` is the one count on the indexing card that means a human has to act,
 * and a bare number says nothing about what that action is. The breakdown turns
 * it into "2 could not be read, 1 missing from storage" — where the first needs
 * a replacement file and the second a re-upload.
 *
 * `reason` is a **stable but open** vocabulary: the backend classifies new
 * failure causes over time, and every unmapped code (including the legacy
 * `unknown` written before reasons were recorded) has to land on generic copy
 * rather than leak a machine code into the UI.
 */
export interface IndexingFailureReason {
  reason: string;
  count: number;
}

export interface ParkedReasonLine {
  key: string;
  label: string;
}

const LABEL_KEYS: Record<string, string> = {
  unreadable_file: 'ai-parked-reason-unreadable-file',
  file_missing: 'ai-parked-reason-file-missing',
  completion_failed: 'ai-parked-reason-completion-failed',
};

const FALLBACK_LABEL_KEY = 'ai-parked-reason-other';

// How a breakdown line is numbered when there is more than one cause. With only
// one there is no number at all: it accounts for every parked document, so the
// headline above already gave the count, and repeating it ("6 documents need to
// be re-indexed manually" / "6 missing from storage") reads as two separate
// groups of six rather than as one group and its cause.
const COUNTED_LINE_KEY = 'ai-parked-reason-line';

/**
 * One code's copy — the card's breakdown lines and the per-document rows behind
 * them read the same vocabulary, so an unmapped code lands on the same generic
 * line in both rather than leaking a machine code into one of them.
 *
 * `reason` is nullable because the per-document field (`aiIndexingError`) is:
 * a document parked before reasons were recorded carries none.
 */
export function getParkedReasonLabel(
  reason: string | null | undefined,
  t: TFunction,
): string {
  return t((reason && LABEL_KEYS[reason]) || FALLBACK_LABEL_KEY);
}

export default function getParkedReasonLines(
  reasons: readonly IndexingFailureReason[] | null | undefined,
  t: TFunction,
): ParkedReasonLine[] {
  if (!reasons?.length) return [];
  // Server order is commonest first, and the counts already sum to `parked` —
  // neither is re-derived here.
  const lines = reasons.filter(({ count }) => count > 0);
  return lines.map(({ reason, count }) => {
    const label = getParkedReasonLabel(reason, t);
    return {
      key: reason,
      label:
        lines.length > 1
          ? t(COUNTED_LINE_KEY, { count, reason: label })
          : label,
    };
  });
}
